import {
  type TransferEligibilityRealEstate,
  type TransferReasonCode,
} from "@/features/salesforceIntegration/types";

/** 除外された物件を理由ごとにまとめたもの。確認ダイアログの内訳に使う。 */
export interface ExcludedTransferReason {
  reasonCode: TransferReasonCode | null;
  reasonMessage: string;
  count: number;
}

export interface TransferSelectionSummary {
  /** 選択された行数。送信件数とは一致しないため、確認ダイアログで並べて示す。 */
  selectedRowCount: number;
  /** 実際に送る物件の識別子。重複は取り除く。 */
  targetUniqueIds: string[];
  /** 送らない物件の内訳。表示用の文言は`excludedReasonLabel`が整える。 */
  excludedReasons: ExcludedTransferReason[];
}

interface SelectedRow {
  uniqueIds: readonly string[];
}

/**
 * 選択された行から、実際にSalesforceへ送る物件と除外される物件を数える。
 *
 * 連携できない物件を選択から弾くのではなく、選ばせたうえで送信対象から外す。
 * チェックボックスは再取得・参謀くん連携と共有しており、Salesforceの都合で
 * 選択可否を変えると他の操作に影響するためである（参謀くん連携と同じ方式）。
 */
export const summarizeTransferSelection = (
  selectedRows: readonly SelectedRow[],
  realEstateByUniqueId: ReadonlyMap<string, TransferEligibilityRealEstate>
): TransferSelectionSummary => {
  const seen = new Set<string>();
  const targetUniqueIds: string[] = [];
  // 理由の初出順を保つためMapで集計する。理由コードが無いときだけ文言をキーにする
  const excludedByReason = new Map<string, ExcludedTransferReason>();

  selectedRows.forEach((row) => {
    row.uniqueIds.forEach((uniqueId) => {
      if (seen.has(uniqueId)) return;
      seen.add(uniqueId);

      const realEstate = realEstateByUniqueId.get(uniqueId);
      // 判定結果に無い物件は送らない。判定APIは同じリクエストの全物件を返すため、
      // ここに来るのは判定結果の取得前だけである
      if (realEstate === undefined) return;

      if (realEstate.isTransferable) {
        targetUniqueIds.push(uniqueId);
        return;
      }

      const reasonMessage = realEstate.reasonMessage ?? "";
      const key = realEstate.reasonCode ?? reasonMessage;
      const excluded = excludedByReason.get(key);
      if (excluded === undefined) {
        excludedByReason.set(key, {
          reasonCode: realEstate.reasonCode,
          reasonMessage,
          count: 1,
        });
        return;
      }
      excluded.count += 1;
    });
  });

  return {
    selectedRowCount: selectedRows.length,
    targetUniqueIds,
    excludedReasons: [...excludedByReason.values()],
  };
};
